import { Injectable, Injector } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';

import { ToastComponent } from './toast.component';
import { ToastData } from './toast-config';
import { ToastRef } from './toast-ref';
import { last } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts: ToastRef[] = [];

  constructor(
    private overlay: Overlay,
    private parentInjector: Injector,
  ) { }

  success(text: string): ToastRef {
    return this.show({type: 'success', text});
  }

  danger(text: string): ToastRef {
    return this.show({type: 'danger', text});
  }

  info(text: string): ToastRef {
    return this.show({type: 'info', text});
  }

  show(data: ToastData) {
    const positionStrategy = this.getPositionStrategy(this.lastToast);
    const overlayRef = this.overlay.create({ positionStrategy });

    const toastRef = new ToastRef(overlayRef);
    this.toasts.push(toastRef);

    const injector = this.getInjector(data, toastRef, this.parentInjector);
    const toastPortal = new ComponentPortal(ToastComponent, null, injector);

    overlayRef.attach(toastPortal);
    overlayRef.detachments().subscribe(() => {
      setTimeout(() => {
        this.toasts.splice(this.toasts.indexOf(toastRef), 1);
        if (this.toasts.length === 0) {
          return;
        }
        let previousToast = null;
        for (const toast of this.toasts) {
          toast.updatePosition(this.getPositionStrategy(previousToast));
          previousToast = toast;
        }
      }, 100);
    });

    if (this.toasts.length > 3) {
      for (const toast of this.toasts.slice(0, -3)) {
        toast.close();
      }
    }

    return toastRef;
  }

  getPositionStrategy(lastToast: ToastRef) {
    if (lastToast && lastToast.isVisible()) {
      if (lastToast.getPosition().top <= 0) return;
      return this.overlay.position()
        .global()
        .top(this.getPosition(lastToast))
        .centerHorizontally();
    } else {
      return this.overlay.position()
        .global()
        .bottom('40px')
        .centerHorizontally();
    }
  }

  getPosition(lastToast: ToastRef) {
    const lastToastIsVisible = lastToast && lastToast.isVisible();
    const position = lastToastIsVisible
      ? lastToast.getPosition().top - lastToast.getPosition().height
      : '0';

    return position + 'px';
  }

  get lastToast() {
    return this.toasts.length && this.toasts[this.toasts.length - 1];
  }

  getInjector(data: ToastData, toastRef: ToastRef, parentInjector: Injector) {
    const tokens = new WeakMap();

    tokens.set(ToastData, data);
    tokens.set(ToastRef, toastRef);

    return new PortalInjector(parentInjector, tokens);
  }
}
